import { useMemo } from 'react';

import { useGetUserSettingsQuery } from '@/store/slices/api/userApiSlice';

import { Filters } from '@/types/globalFilter';
import { DeviceVendor, getVendorDisplayName } from '@/types/matchDevice';

const mapPractitionerToLabelValue = (practitioner: {
  given?: string;
  family?: string;
  id: string;
}) =>
  practitioner.id === 'null'
    ? { label: '-', value: 'null' }
    : {
        label: `${practitioner?.given ?? ''} ${practitioner?.family ?? ''}`.trim(),
        value: practitioner.id
      };

/**
 * Derives the same `Filters` shape that used to be seeded once (at login) into the legacy
 * `preferencesSlice.filters` ("global filter") Redux snapshot — but computed directly, and
 * always freshly, from the `/user-settings` RTK Query cache.
 *
 * Use this in filter-editing UI (dropdowns/modals for clinics, practitioners, device types,
 * vendors, patient statuses) instead of reading `state.preferences.filters` /
 * `state.preferences.userSettingsId`, so defaults/options always reflect the latest saved
 * settings — and automatically refresh after any `updateUserSettings`/`updatePagePreferences`
 * mutation (which invalidates the `getUserSettings` cache tag) — rather than a stale,
 * manually-synced copy.
 */
export const useUserSettingsFilters = () => {
  const {
    data: userSettingsData,
    isLoading,
    isFetching,
    isError,
    refetch
  } = useGetUserSettingsQuery();

  const filters: Filters | null = useMemo(() => {
    if (!userSettingsData) return null;

    return {
      clinics:
        userSettingsData.filteredClinics?.map((clinic) => ({
          label: clinic.name,
          value: clinic.id
        })) ?? [],
      practitioners: userSettingsData.filteredPractitioners?.map(mapPractitionerToLabelValue) ?? [],
      signingPractitioners:
        userSettingsData.filteredSigningPractitioners?.map(mapPractitionerToLabelValue) ?? [],
      deviceTypes:
        userSettingsData.deviceTypes?.map((deviceType) => ({
          label: deviceType,
          value: deviceType
        })) ?? [],
      vendors:
        userSettingsData.vendors?.map((vendor) => ({
          label: getVendorDisplayName(vendor as DeviceVendor),
          value: vendor
        })) ?? [],
      patientStatuses:
        userSettingsData.patientStatuses?.map((status) => ({
          label: status,
          value: status
        })) ?? []
    };
  }, [userSettingsData]);

  return {
    filters,
    userSettingsId: userSettingsData?.filterId ?? null,
    userSettingsData,
    isLoading,
    isFetching,
    isError,
    refetchUserSettings: refetch
  };
};

export default useUserSettingsFilters;
